/**
 * Erros que os usuários encontraram.
 *
 * O modelo é o do sistema atual, que o Lucas quis manter porque funciona:
 * captura SEMPRE, silenciosamente; avisar é opcional e parte da pessoa. Daí a
 * separação entre reportado e não reportado — e a aba que mais importa é a de
 * NÃO reportados, porque é onde mora o erro que ninguém contou.
 *
 * Resolvido sai das duas listas de propósito. Se ficasse, a fila nunca
 * esvaziaria, e fila que nunca esvazia é fila que se aprende a ignorar.
 */

import { api, ErroApi } from '../../api.js';
import { escapar, dataHora } from '../../comum/formato.js';
import { avisar, avisarBloqueante } from '../../comum/aviso.js';

interface Erro {
  id: number;
  ocorrido_em: string;
  modulo: string | null;
  usuario: string | null;
  acao: string | null;
  mensagem: string;
  contexto: Record<string, unknown> | null;
  reportado_em: string | null;
  resolvido: boolean;
}

interface Resposta {
  erros: Erro[];
  contagem: { total: number; reportados: number; naoReportados: number; resolvidos: number };
}

export function montarErros(raiz: HTMLElement, aoMudar: () => void): void {
  let situacao: 'nao_reportados' | 'reportados' | 'resolvidos' | 'todos' = 'nao_reportados';
  let busca = '';

  raiz.innerHTML = `
    <div class="chips" id="abas-erro"></div>
    <div class="barra-busca">
      <input id="erro-busca" placeholder="Procure na mensagem, na ação ou em quem viu…" autocomplete="off">
    </div>
    <div id="erro-lista"><p class="carregando">Carregando…</p></div>`;

  const lista = raiz.querySelector<HTMLElement>('#erro-lista')!;

  let temporizador: number | undefined;
  raiz.querySelector<HTMLInputElement>('#erro-busca')!.addEventListener('input', (e) => {
    window.clearTimeout(temporizador);
    const v = (e.target as HTMLInputElement).value.trim();
    temporizador = window.setTimeout(() => {
      busca = v;
      void carregar();
    }, 300);
  });

  raiz.querySelector('#abas-erro')!.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-sit]');
    if (!b) return;
    situacao = b.dataset['sit'] as typeof situacao;
    void carregar();
  });

  function desenharAbas(c: Resposta['contagem']) {
    raiz.querySelector<HTMLElement>('#abas-erro')!.innerHTML = (
      [
        ['nao_reportados', 'Não reportados', c.naoReportados],
        ['reportados', 'Reportados', c.reportados],
        ['resolvidos', 'Resolvidos', c.resolvidos],
        ['todos', 'Todos', c.total],
      ] as [typeof situacao, string, number][]
    )
      .map(
        ([v, t, n]) =>
          `<button class="chip" data-sit="${v}" aria-pressed="${situacao === v}">
             ${t}${n ? ` <span class="conta">${n}</span>` : ''}
           </button>`,
      )
      .join('');
  }

  async function carregar() {
    try {
      const p = new URLSearchParams({ situacao });
      if (busca) p.set('busca', busca);
      const r = await api.get<Resposta>(`/api/master/erros?${p}`);
      desenharAbas(r.contagem);
      desenhar(r.erros);
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar os erros.',
      )}</p>`;
    }
  }

  function desenhar(erros: Erro[]) {
    if (erros.length === 0) {
      lista.innerHTML = `<div class="vazio"><p>${
        situacao === 'nao_reportados'
          ? 'Nenhum erro em aberto que ninguém reportou. É o estado bom.'
          : 'Nada nesta lista.'
      }</p></div>`;
      return;
    }

    lista.innerHTML = erros
      .map(
        (e) => `
      <article class="erro-cartao ${e.reportado_em ? 'reportado' : ''} ${e.resolvido ? 'resolvido' : ''}">
        <header>
          <span class="erro-quando">${dataHora(e.ocorrido_em)}</span>
          ${e.modulo ? `<span class="erro-modulo">${escapar(e.modulo)}</span>` : ''}
          ${e.reportado_em ? `<span class="erro-selo">reportado pelo usuário</span>` : ''}
          ${e.resolvido ? `<span class="erro-selo ok">resolvido</span>` : ''}
        </header>
        <p class="erro-msg">${escapar(e.mensagem)}</p>
        <p class="erro-meta">
          ${e.acao ? `ao ${escapar(e.acao)}` : ''}
          ${e.usuario ? ` · ${escapar(e.usuario)}` : ''}
        </p>
        ${
          e.contexto
            ? `<details class="erro-ctx"><summary>Contexto técnico</summary>
                 <pre>${escapar(JSON.stringify(e.contexto, null, 1))}</pre></details>`
            : ''
        }
        <button class="btn btn-claro" data-resolver="${e.id}" data-para="${e.resolvido ? 'false' : 'true'}">
          ${e.resolvido ? 'Reabrir' : 'Marcar como resolvido'}
        </button>
      </article>`,
      )
      .join('');

    lista.querySelectorAll<HTMLElement>('[data-resolver]').forEach((b) => {
      b.addEventListener('click', async () => {
        try {
          await api.patch(`/api/master/erros/${b.dataset['resolver']}`, {
            resolvido: b.dataset['para'] === 'true',
          });
          avisar(b.dataset['para'] === 'true' ? 'Erro marcado como resolvido' : 'Erro reaberto');
          await carregar();
          // O contador do painel e o badge da aba dependem disto.
          aoMudar();
        } catch (e) {
          avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui alterar.');
        }
      });
    });
  }

  void carregar();
}
